import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { AppDispatch } from "src/store";
import { useLazyGetBookingDetailQuery, type BookingDetailResult } from "src/store/apis/Hotels";
import { expireStoredSession, getStoredData } from "src/utility";
import { pollDriver, POST_PAYMENT_POLL_DELAY } from "./utils";
import { OrderStatus } from "src/Modules/UserHistory/constants/OrdersList";
import { cancelTimer, expireTicket } from "src/store/slices/timer";
import PostbookLoader from "./components/PostbookLoader/PostbookLoader";
import HotelBookingResultNewWeb from "./layouts/HotelBookingResultNew.web";

export default function HotelBookingResult() {
  const { t } = useTranslation();
  const { hotelDetailsKey } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();

  const referenceNumber = getStoredData(hotelDetailsKey ?? "", "referenceNumber");

  const [triggerGetBookingDetail, { data: bookingDetail }] = useLazyGetBookingDetailQuery();
  const [finalStatus, setFinalStatus] = useState<number>();
  useEffect(() => {
    const endSession = () => {
      dispatch(cancelTimer());
      dispatch(expireTicket());
      expireStoredSession(hotelDetailsKey);
    };
    const pollBookingStatus = () => {
      if (!referenceNumber) return;
      pollDriver<BookingDetailResult>({
        functionToPoll: () => triggerGetBookingDetail({ referenceNumber }).unwrap(),
        shouldStop: (result) => result && result.orderStatus != undefined && result.orderStatus !== OrderStatus.Pending,
        initialDelay: searchParams.get("fromPayment") === "true" ? POST_PAYMENT_POLL_DELAY : 0,
      })
        .then((result) => setFinalStatus(result?.orderStatus ?? OrderStatus.Failed))
        .catch(() => setFinalStatus(OrderStatus.Failed));
    };

    endSession();
    pollBookingStatus();
  }, []);

  const isPolling = !!referenceNumber && finalStatus === undefined;
  const isSuccess = finalStatus !== undefined;

  if (isPolling) return <PostbookLoader />;

  if (!bookingDetail || !isSuccess) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="text-font-default text-2xl font-bold">{t("Hotels.bookingResult.bookingNotFound")}</div>
          <div className="text-font-subtle">{t("Hotels.bookingResult.bookingNotFoundSubtitle")}</div>
          <Link to="/hotel" className="bg-background-primary text-font-inverted font-inter rounded-sm px-5 py-2.5">
            {t("Hotels.bookingResult.backToSearch")}
          </Link>
        </div>
      </div>
    );
  }

  return <HotelBookingResultNewWeb bookingDetail={bookingDetail} />;
}
